import {
  useReactTable,
  getCoreRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getSortedRowModel,   SortingState,
  PaginationState,
  ExpandedState,
  Updater,
} from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";

import { TableView } from "ui/data-grid/components/table-view/table-view";
import { SortableTableProvider } from "ui/data-grid/components/sortable-context/sortable-context";
import { TablePagination } from "ui/table-pagination/table-pagination";

import { DataGridProps } from "ui/data-grid/data-grid.types";

export const DataGrid = <T, TEmptyGroupData = unknown>({
  columns,
  data,
  isLoading = false,
  totalRecords = 0,
  sortingParams,
  manualSorting = true,
  setSortingParams,
  isMultiSort = false,
  getGroupedNode,
  getEmptyGroupNode,
  grouping = [],
  enableGrouping = false,
  groupedColumnMode,   isExpandable = false,
  expandedState,
  onExpandedChange = () => {},
  emptyGroups = [],
  pagination = true,
  pageIndex = 0,
  pageSize = 0,
  setPageIndex = () => {},
  setAmount = () => {},
  pagesAmount = 0,
  showingRecordsIsOn = false,
  viewByAmountSelectIsOn = false,
  isDraggable = false,
  onReorder,
  draggableId,
  tableId,
  styles,
  pinPrimaryColumnLeft = false,
  fillHeight = false,
  emptyMessage,
  emptyState,
  skeletonRowCount,
}: DataGridProps<T, TEmptyGroupData>) => {
  const onPaginationChange = (updater: Updater<PaginationState>) => {
    const newPaginationState =
      typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;

    setPageIndex?.(newPaginationState.pageIndex);
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    const newSortingState = typeof updater === "function" ? updater(sortingParams ?? []) : updater;
    setSortingParams?.(newSortingState);
  };

  const handleExpandedChange = (updater: Updater<ExpandedState>) => {
    const newExpandedState = typeof updater === "function" ? updater(expandedState ?? {}) : updater;
    onExpandedChange?.(newExpandedState);
  };

  const table = useReactTable({
    data,
    columns,
    manualSorting,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    pageCount: pagesAmount,
    state: {
      sorting: sortingParams,
      pagination: { pageIndex, pageSize },
      grouping: enableGrouping ? grouping : undefined,
      expanded: isExpandable && enableGrouping ? (expandedState ?? true) : undefined,
    },
    onPaginationChange: onPaginationChange,
    onExpandedChange: handleExpandedChange,
    enableSortingRemoval: false,
    onSortingChange: onSortingChange,
    enableMultiSort: isMultiSort,
    isMultiSortEvent: () => true,
    maxMultiSortColCount: 2,
    autoResetExpanded: false,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: enableGrouping ? getExpandedRowModel() : undefined,
    enableGrouping: enableGrouping,
    groupedColumnMode: enableGrouping ? groupedColumnMode : false,
  });

  const handleRowsPerPageChange = (newPageSize: number) => {
    setAmount({ pageSize: newPageSize, totalRecords });
    table.setPageSize(newPageSize);
  };

  const tableContent = (
    <div className={twMerge("flex w-full flex-col gap-2.5", fillHeight && "min-h-0 min-w-0 flex-1")}>
      <TableView
        table={table}
        getGroupedNode={getGroupedNode}
        getEmptyGroupNode={getEmptyGroupNode}
        emptyGroups={emptyGroups}
        grouping={grouping}
        sortingParams={sortingParams ?? []}
        isLoading={isLoading}
        isMultiSort={isMultiSort}
        isDraggable={isDraggable}
        draggableId={draggableId}
        tableId={tableId}
        styles={styles}
        pinPrimaryColumnLeft={pinPrimaryColumnLeft}
        fillHeight={fillHeight}
        emptyMessage={emptyMessage}
        emptyState={emptyState}
        skeletonRowCount={skeletonRowCount}
      />
      {!!pagination && (
        <div className="flex shrink-0 items-center justify-end">
          <TablePagination
            count={totalRecords}
            page={pageIndex}
            rowsPerPage={pageSize}
            onPageChange={table.setPageIndex}
            onRowsPerPageChange={viewByAmountSelectIsOn ? handleRowsPerPageChange : undefined}
            showDisplayedRows={showingRecordsIsOn}
          />
        </div>
      )}
    </div>
  );

  if (isDraggable) {
    if (!draggableId) {
      throw new Error("draggableId is required when isDraggable is true");
    }
    if (!tableId) {
      throw new Error("tableId is required when isDraggable is true");
    }
    return (
      <SortableTableProvider
        items={data}
        onReorder={onReorder}
        idField={draggableId}
        tableId={tableId}>
        {tableContent}
      </SortableTableProvider>
    );
  }

  return tableContent;
};
